import { PageHeader, LoadingState, DataTable, EmptyState } from '../components/AdminPageUI';
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Button, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, IconButton, Card, CardContent,
  Container, Avatar, Stack, Tooltip, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert,
  Collapse, Paper, Divider, useMediaQuery
} from '@mui/material';
import {
  Add, Announcement, Campaign, Person, Group,
  ExpandMore, ExpandLess, Schedule, Public, Delete
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import api from '../utils/Api';

// 🟢 OPTIONAL HELPER (for consistency across files)
const getClassLabel = (cls) => cls?.displayName || cls?.name || '';

export default function AnnouncementsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [announcements, setAnnouncements] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [form, setForm] = useState({
    title: '',
    message: '',
    targetRoles: [],
    classId: ''
  });

  // FIXED - handles both string and object formats safely
  const schoolId = (() => {
    try {
      const raw = localStorage.getItem("user");
      if (!raw) return null;

      const user = JSON.parse(raw);

      // Accept either string or object {_id}
      return user.school?._id || user.school || null;
    } catch (err) {
      console.error("Failed to read user from localStorage:", err);
      return null;
    }
  })();


  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const res = await api.get('/announcements', {
        params: { school: schoolId }
      });

      // Handle different response formats
      let announcementsData = [];
      if (Array.isArray(res.data)) {
        announcementsData = res.data;
      } else if (res.data && Array.isArray(res.data.announcements)) {
        announcementsData = res.data.announcements;
      } else if (res.data && Array.isArray(res.data.data)) {
        announcementsData = res.data.data;
      } else {
        console.warn('Unexpected announcements response format:', res.data);
        announcementsData = [];
      }

      console.log('Fetched announcements:', announcementsData);
      setAnnouncements(announcementsData);
    } catch (err) {
      console.error('Error fetching announcements:', err);
      toast.error('Failed to load announcements');
      setAnnouncements([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      setClasses(Array.isArray(res.data?.classes) ? res.data.classes : []);
    } catch (err) {
      console.error('Error fetching classes:', err);
      toast.error('Failed to load classes');
    }
  };

  useEffect(() => {
    if (schoolId) {
      fetchClasses();
      fetchAnnouncements();
    } else {
      toast.error('School information not found');
    }
  }, [schoolId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleRoleChange = (e) => {
    const { value } = e.target;
    setForm({ ...form, targetRoles: typeof value === 'string' ? value.split(',') : value });
  };

  const handleSubmit = async () => {
    if (!form.title || !form.message || form.targetRoles.length === 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    setFormLoading(true);
    try {
      const payload = {
        ...form,
        school: schoolId
      };

      const res = await api.post('/announcements', payload);
      toast.success('Announcement created successfully');
      setForm({ title: '', message: '', targetRoles: [], classId: '' });
      setOpen(false);
      fetchAnnouncements();
    } catch (err) {
      console.error('Error adding announcement:', err.response?.data || err.message);
      toast.error(err.response?.data?.message || 'Failed to create announcement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement? This will remove it for everyone.')) return;
    
    try {
      await api.delete(`/announcements/${id}`);
      toast.success('Announcement deleted successfully');
      fetchAnnouncements();
    } catch (err) {
      console.error('Error deleting announcement:', err);
      toast.error(err.response?.data?.message || 'Failed to delete announcement');
    }
  };

  const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'A';

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'student': return 'primary';
      case 'parent': return 'secondary';
      case 'teacher': return 'success';
      default: return 'default';
    }
  };

  const resetForm = () => {
    setForm({ title: '', message: '', targetRoles: [], classId: '' });
  };

  // Mobile-optimized announcement card
  const MobileAnnouncementCard = ({ announcement }) => (
    <Card sx={{ mb: 2, boxShadow: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Box>
            <Typography variant="h6" fontWeight="600" gutterBottom>
              {announcement.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {announcement.message}
            </Typography>
          </Box>

          {/* Target Audience */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              TARGET AUDIENCE
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {announcement.targetRoles && announcement.targetRoles.map((role) => (
                <Chip
                  key={role}
                  label={role.charAt(0).toUpperCase() + role.slice(1)}
                  color={getRoleColor(role)}
                  size="small"
                  variant="outlined"
                  sx={{ mb: 0.5 }}
                />
              ))}
              {/* 🔴 FUTURE-PROOFING: When class info is added to announcements */}
              {announcement.class && (
                <Chip
                  label={`Class: ${getClassLabel(announcement.class)}`}
                  color="default"
                  size="small"
                  variant="outlined"
                  sx={{ mb: 0.5 }}
                />
              )}
            </Stack>
          </Box>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="row" alignItems="center" spacing={1}>
              <Avatar sx={{
                bgcolor: theme.palette.primary.main,
                width: 28,
                height: 28,
                fontSize: '0.75rem'
              }}>
                {getInitials(announcement.sentBy?.name)}
              </Avatar>
              <Typography variant="body2" fontSize="0.8rem">
                {announcement.sentBy?.name || 'System'}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {formatDate(announcement.createdAt || announcement.date)}
              </Typography>
              <IconButton 
                size="small" 
                color="error" 
                onClick={() => handleDelete(announcement._id)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 1 : 2 }}>
      {/* Header - Stack on mobile */}
      <PageHeader title={<>Announcements</>} description={<>Create and manage school-wide announcements</>} actions={<><Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)} fullWidth={isMobile} size={isMobile ? "medium" : "large"}>
                    Create Announcement
                  </Button></>}></PageHeader>

      {/* Action Card */}
      

      {/* Announcements List */}
      <Card sx={{
        boxShadow: isMobile ? 1 : 3,
        border: `1px solid ${theme.palette.divider}`
      }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            p: isMobile ? 2 : 3,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Stack
              direction={isMobile ? "column" : "row"}
              justifyContent="space-between"
              alignItems={isMobile ? "flex-start" : "center"}
              spacing={isMobile ? 1 : 0}
            >
              <Typography variant={isMobile ? "h6" : "h6"} fontWeight="600">
                Recent Announcements ({announcements.length})
              </Typography>
              {announcements.length > 0 && (
                <Chip
                  label={`${announcements.length} Total`}
                  color="primary"
                  variant="outlined"
                  size={isMobile ? "small" : "medium"}
                />
              )}
            </Stack>
          </Box>

          {loading ? (
            <LoadingState />
          ) : announcements.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6, px: 2 }}>
              <Announcement sx={{
                fontSize: isMobile ? 48 : 64,
                color: 'text.secondary',
                opacity: 0.5,
                mb: 2
              }} />
              <Typography variant={isMobile ? "h6" : "h6"} color="text.secondary" gutterBottom>
                No announcements found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first announcement to get started
              </Typography>
            </Box>
          ) : isMobile ? (
            // Mobile View - Cards
            <Box sx={{ p: 2 }}>
              <Stack spacing={2}>
                {announcements.map((announcement) => (
                  <MobileAnnouncementCard
                    key={announcement._id}
                    announcement={announcement}
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            // Desktop/Tablet View - Table
            <Box sx={{
              width: '100%',
              overflow: 'auto',
              maxHeight: isTablet ? '60vh' : 'none'
            }}>
              <DataTable><Table sx={{ minWidth: isTablet ? 800 : 'auto' }}>
                <TableHead sx={{ bgcolor: theme.palette.background.default }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>ANNOUNCEMENT</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>TARGET AUDIENCE</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>AUTHOR</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', py: 2 }}>DATE CREATED</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', py: 2 }}>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
{announcements.length === 0 && <TableRow><TableCell colSpan={100}><EmptyState title="No records found" description="Records will appear here when available. Check your selected filters." /></TableCell></TableRow>}

                  {announcements.map((announcement) => (
                    <TableRow
                      key={announcement._id}
                      sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}
                    >
                      <TableCell sx={{ py: 2, maxWidth: isTablet ? 200 : 300 }}>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="500" gutterBottom noWrap>
                            {announcement.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden'
                            }}
                          >
                            {announcement.message}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {announcement.targetRoles && announcement.targetRoles.map((role) => (
                            <Chip
                              key={role}
                              label={role.charAt(0).toUpperCase() + role.slice(1)}
                              color={getRoleColor(role)}
                              size="small"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          ))}
                          {/* 🔴 FUTURE-PROOFING: When class info is added to announcements */}
                          {announcement.class && (
                            <Chip
                              label={`Class: ${getClassLabel(announcement.class)}`}
                              color="default"
                              size="small"
                              variant="outlined"
                              sx={{ mb: 0.5 }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Avatar sx={{
                            bgcolor: theme.palette.primary.main,
                            width: 32,
                            height: 32,
                            fontSize: '0.75rem'
                          }}>
                            {getInitials(announcement.sentBy?.name)}
                          </Avatar>
                          <Typography variant="body2" fontSize="0.875rem">
                            {announcement.sentBy?.name || 'System'}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography variant="body2" fontSize="0.875rem">
                          {formatDate(announcement.createdAt || announcement.date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ py: 2 }}>
                        <Tooltip title="Delete Announcement">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDelete(announcement._id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table></DataTable>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create Announcement Dialog - Full screen on mobile */}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            minHeight: isMobile ? '100vh' : '60vh'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: isMobile ? 2 : 3
        }}>
          <Campaign />
          Create New Announcement
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: isMobile ? 2 : 4 }}>
            <Stack spacing={3}>
              {/* Title Field */}
              <Box>
                <TextField
                  fullWidth
                  label="Announcement Title"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                  placeholder="Enter a clear and concise title"
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 600, mb: 1 }
                  }}
                  helperText="This will be the main heading of your announcement"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                />
              </Box>

              {/* Message Field */}
              <Box>
                <TextField
                  fullWidth
                  label="Message Content"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  multiline
                  rows={isMobile ? 4 : 6}
                  placeholder="Enter the detailed message for your announcement..."
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 600, mb: 1 }
                  }}
                  helperText="Provide all the necessary details for your audience"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    },
                    '& .MuiOutlinedInput-input': {
                      lineHeight: 1.5
                    }
                  }}
                />
              </Box>

              <Stack spacing={2}>
                {/* Target Audience Field */}
                <TextField
                  select
                  fullWidth
                  label="Target Audience"
                  name="targetRoles"
                  value={form.targetRoles}
                  onChange={handleRoleChange}
                  required
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 600, mb: 1 }
                  }}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => {
                      if (selected.length === 0) {
                        return 'Select Target Roles';
                      }
                      return selected.map(role =>
                        role.charAt(0).toUpperCase() + role.slice(1)
                      ).join(', ');
                    }
                  }}
                  helperText={
                    form.targetRoles.length === 0
                      ? 'Choose who should receive this announcement'
                      : `${form.targetRoles.length} role(s) selected`
                  }
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    Select Target Roles
                  </MenuItem>
                  {['student', 'parent', 'teacher'].map((role) => (
                    <MenuItem key={role} value={role}>
                      {role.charAt(0).toUpperCase() + role.slice(1)}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Class Selection Field */}
                <TextField
                  select
                  fullWidth
                  label="Select Class (Optional)"
                  name="classId"
                  value={form.classId}
                  onChange={handleChange}
                  placeholder="Select a class"
                  InputLabelProps={{
                    shrink: true,
                    sx: { fontWeight: 600, mb: 1 }
                  }}
                  helperText="Leave blank for school-wide announcements"
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1
                    }
                  }}
                >
                  <MenuItem value="">
                    <em>All Classes (School-wide)</em>
                  </MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls._id} value={cls._id}>
                      {cls.displayName || cls.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {/* Target Audience Preview */}
              {form.targetRoles.length > 0 && (
                <Alert
                  severity="info"
                  sx={{
                    borderRadius: 1,
                    '& .MuiAlert-message': {
                      width: '100%'
                    }
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" gutterBottom fontWeight="600">
                      Announcement will be sent to:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {form.targetRoles.map((role) => (
                        <Chip
                          key={role}
                          label={role.charAt(0).toUpperCase() + role.slice(1)}
                          color={getRoleColor(role)}
                          size="small"
                          variant="filled"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                      {/* Class Preview */}
                      {form.classId && (
                        <Chip
                          label={`Class: ${classes.find(c => c._id === form.classId)?.displayName || classes.find(c => c._id === form.classId)?.name || 'Unknown Class'}`}
                          color="default"
                          size="small"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      )}
                    </Stack>
                  </Box>
                </Alert>
              )}
            </Stack>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: isMobile ? 2 : 3,
          gap: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          flexDirection: isMobile ? 'column' : 'row'
        }}>
          <Button
            onClick={() => {
              setOpen(false);
              resetForm();
            }}
            variant="outlined"
            fullWidth={isMobile}
            sx={{
              minWidth: isMobile ? 'auto' : 120,
              borderRadius: 1
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={formLoading}
            fullWidth={isMobile}
            startIcon={formLoading ? <CircularProgress size={20} /> : null}
            sx={{
              minWidth: isMobile ? 'auto' : 160,
              borderRadius: 1
            }}
          >
            {formLoading ? 'Creating...' : 'Create Announcement'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}